import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import ContactForm from '../components/ContactForm';
import GallerySection from '../components/GallerySection';
import { getCdnImage } from '../lib/imageUtils';

export default function ServiceSewer() {
  const serviceItems = [
    {
      title: 'Komplet kloaktilslutning ved nybyggeri, renovering og separering',
      desc: 'Vi står for hele tilslutningsforløbet – fra ansøgning og dimensionering til nedgravning, tilslutning og færdigmelding til kommunen.',
      icon: 'fa-solid fa-arrows-split-up-and-left',
    },
    {
      title: 'Etablering og montering af godkendte pumpestationer og samletanke',
      desc: 'Kvalitetsløsninger til spildevand og regnvand, hvor terrænet eller afløbsforholdene kræver tryksat pumpning eller opsamling.',
      icon: 'fa-solid fa-gears',
    },
    {
      title: 'Montering af rottespærrer',
      desc: 'Effektiv sikring mod rotteindtrængen med autoriseret montering af godkendte rottespærrer i brønde samt service og eftersyn.',
      icon: 'fa-solid fa-shield-virus',
    },
    {
      title: 'Fejlfinding og reparation af kloaksystemer',
      desc: 'Lokaliserer stoppede rør, brud og rottehuller hurtigt og foretager holdbare punkt-reparationer eller udskiftning.',
      icon: 'fa-solid fa-wrench',
    },
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <SeoMeta
        title="Autoriseret kloakarbejde & tilslutning - ENTREPRENØR LANG ApS"
        description="Alt inden for autoriseret kloakmesterarbejde til private og erhverv. Kloaktilslutning, pumpestationer, samletanke og rottespærrer."
        keywords="kloakmester, autoriseret kloakarbejde, kloaktilslutning, pumpestationer, samletanke, rottespærre, Lolland, Falster, Øster Ulslev"
      />

      {/* Hero */}
      <section className="relative bg-slate-950 text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/autoriseret-kloakarbejde-roer.jpg', {
              width: 1920,
              quality: 80,
            })}
            alt="Autoriseret kloakarbejde og rørføring"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors uppercase tracking-wider"
            >
              <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
              <span>Tilbage til alle ydelser</span>
            </Link>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              Autoriseret kloakarbejde & tilslutning
            </h1>
            <p className="text-lg text-slate-200 leading-relaxed font-medium">
              Alt inden for autoriseret kloakmesterarbejde til private og erhverv.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="#kontakt"
                className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow transition-all"
              >
                Få et tilbud
              </a>
              <a
                href="tel:40309708"
                className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-white/20 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone mr-2 text-emerald-400" aria-hidden="true" />
                Ring 40 30 97 08
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-6">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-2">
                <i className="fa-solid fa-shield-halved text-xs" aria-hidden="true" />
                Autoriseret kloakmester
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Tryghed og garanti på alt kloakarbejde
              </h2>
              <p className="text-slate-600 text-base mt-3 leading-relaxed">
                Kloakarbejde er autorisationskrævende arbejde, der kræver høj faglighed og kendskab til gældende miljø- og afløbsnormer. ENTREPRENØR LANG ApS er autoriseret kloakmester og tilknyttet godkendt kvalitetsstyring, så du altid er garanteret korrekt udført arbejde og gældende dokumentation.
              </p>
            </div>

            {/* Quality Seals Card */}
            <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4">
                <img
                  src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/dme-kloakmestergaranti.webp', {
                    width: 480,
                    quality: 90,
                  })}
                  alt="DM&E Kloakmestergaranti"
                  className="h-28 w-auto object-contain flex-shrink-0"
                />
                <div>
                  <h4 className="font-bold text-slate-900 text-base">DM&E Kloakmestergaranti</h4>
                  <p className="text-sm text-slate-500">Tryghed og garanti på autoriseret kloakarbejde</p>
                </div>
              </div>
            </div>

            {/* Service Points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {serviceItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-2"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center">
                    <i className={`${item.icon} text-base`} aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-base leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right side visual */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-white">
              <img
                src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-601820261-1366670001827292-7586379579870585065-n.jpg', {
                  width: 800,
                  quality: 85,
                })}
                alt="Nedgravning af jordvarmeslanger på mark med gravemaskine"
                className="w-full h-80 object-cover"
              />
              <div className="p-5">
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                  Jordvarmeanlæg
                </span>
                <p className="text-slate-900 font-bold text-base mt-1">
                  Nedgravning af jordvarmeslanger
                </p>
                <p className="text-slate-600 text-xs sm:text-sm mt-1">
                  Præcis udgravning og professionel nedgravning af jordvarmeslanger med korrekt dybde og underlag.
                </p>
              </div>
            </div>

            <div className="bg-slate-900 text-white p-6 rounded-2xl space-y-3 border border-slate-800">
              <h4 className="font-bold text-base flex items-center gap-2">
                <i className="fa-solid fa-file-signature text-emerald-400" aria-hidden="true" />
                Skal du separere regn- og spildevand?
              </h4>
              <p className="text-slate-300 text-xs sm:text-sm">
                Flere kommuner stiller krav om kloakseparering. Vi rådgiver gerne om den mest omkostningseffektive løsning til din ejendom.
              </p>
              <Link
                to="/kontakt-os"
                className="inline-flex items-center gap-2 text-xs font-bold text-emerald-400 hover:text-emerald-300 pt-1"
              >
                <span>Spørg os om separering</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>

        {/* Dynamic CMS Gallery Section */}
        <GallerySection
          gallerySlug="kloakarbejde"
          title="Billeder fra kloakprojekter"
          subtitle="Dokumentation for afløbsinstallationer, brønde og autoriseret arbejde."
        />
      </section>

      {/* Form */}
      <section id="kontakt" className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactForm defaultService="Autoriseret kloakarbejde & tilslutning" />
        </div>
      </section>
    </div>
  );
}
